import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cashPayments, type CashPaymentListItem } from '@/api/cashDocs';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashPaymentsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['cash-payments'],
    queryFn: () => cashPayments.list({ limit: 200 }),
  });
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColumnDef<CashPaymentListItem>[]>(() => [
    { key: 'document_number', header: t('cd.number'), width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'document_date', header: t('cd.date'), width: 170, sortValue: (r) => r.document_date ?? '', cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString(i18n.language) : '' },
    { key: 'issued_to', header: t('cp.issued_to'), sortValue: (r) => r.issued_to ?? (r.contractor_id ? cName.get(r.contractor_id) ?? '' : ''), cell: (r) => r.issued_to ? r.issued_to : (r.contractor_id ? (cName.get(r.contractor_id) ?? '') : '') },
    { key: 'basis', header: t('cd.basis'), sortValue: (r) => r.basis ?? '', cell: (r) => r.basis },
    { key: 'amount', header: t('cd.amount'), width: 140, align: 'right', sortValue: (r) => Number(r.amount), cell: (r) => fmt(r.amount) },
    { key: 'posted', header: t('cd.posted'), width: 130, align: 'center', sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no') },
  ], [t, i18n.language, cName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('cp.title')}</h2>
        <button className="primary" onClick={() => navigate('/cash-payments/new')}>
          + {t('cp.new')}
        </button>
      </div>
      <DataTable<CashPaymentListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/cash-payments/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
